import { useCoreServices } from '@bangle.io/context';
import {
  $pendingPresenceJump,
  $stickyCloudWorkspace,
  $workspacePresencePeers,
} from '@bangle.io/editor';
import {
  fetchProfilesByIds,
  PROFILE_SOCIAL_LINK_KEYS,
  type ProfileRow,
  type ProfileSocialLinkKey,
  type WorkspacePresencePeer,
} from '@bangle.io/supabase';
import { Button, DropdownMenu, toast } from '@bangle.io/ui-components';
import { useAtomValue, useSetAtom } from 'jotai';
import React from 'react';

const SOCIAL_LABELS: Record<ProfileSocialLinkKey, () => string> = {
  website: () => t.app.settings.account.socialWebsite,
  github: () => t.app.settings.account.socialGithub,
  mastodon: () => t.app.settings.account.socialMastodon,
  bluesky: () => t.app.settings.account.socialBluesky,
  youtube: () => t.app.settings.account.socialYoutube,
  email: () => t.app.settings.account.socialEmail,
};

function ProfileCard({ profile }: { profile: ProfileRow }) {
  const links = PROFILE_SOCIAL_LINK_KEYS.flatMap((key) => {
    const href = profile.social_links?.[key];
    return href ? [{ key, href, label: SOCIAL_LABELS[key]() }] : [];
  });

  return (
    <div className="flex w-64 flex-col gap-2 p-1" data-testid="profile-card">
      <div className="flex items-center gap-2">
        {profile.avatar_url ? (
          <img
            src={profile.avatar_url}
            alt=""
            className="size-9 rounded-full object-cover"
          />
        ) : (
          <div className="flex size-9 items-center justify-center rounded-full bg-muted font-medium text-sm">
            {(profile.display_name || profile.email || '?')
              .slice(0, 1)
              .toUpperCase()}
          </div>
        )}
        <div className="min-w-0">
          <div className="truncate font-medium text-sm">
            {profile.display_name || profile.email || t.app.common.unknown}
          </div>
          {profile.email ? (
            <div className="truncate text-muted-foreground text-xs">
              {profile.email}
            </div>
          ) : null}
        </div>
      </div>
      {profile.bio ? (
        <p className="whitespace-pre-wrap text-muted-foreground text-xs">
          {profile.bio}
        </p>
      ) : null}
      {links.length > 0 ? (
        <ul className="flex flex-col gap-1">
          {links.map((link) => (
            <li key={link.key}>
              <a
                href={link.href}
                target="_blank"
                rel="noreferrer"
                className="text-primary text-xs underline-offset-2 hover:underline"
              >
                {link.label}
              </a>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/** Peer avatars for the cloud workspace. Sync/save feedback lives in NoteSaveStatus. */
export function PresenceAvatars() {
  const core = useCoreServices();
  const peers = useAtomValue($workspacePresencePeers);
  const stickyWorkspace = useAtomValue($stickyCloudWorkspace);
  const setPendingJump = useSetAtom($pendingPresenceJump);
  const [profiles, setProfiles] = React.useState<Record<string, ProfileRow>>(
    {},
  );

  React.useEffect(() => {
    const ids = [...new Set(peers.map((peer) => peer.id).filter(Boolean))];
    if (ids.length === 0) {
      setProfiles({});
      return;
    }
    let cancelled = false;
    void fetchProfilesByIds(ids)
      .then((map) => {
        if (!cancelled) setProfiles(map);
      })
      .catch(() => {
        if (!cancelled) setProfiles({});
      });
    return () => {
      cancelled = true;
    };
  }, [peers]);

  const jumpToPeer = (peer: WorkspacePresencePeer) => {
    if (!peer.filePath) {
      toast.info(t.app.toasts.presenceJumpNoNote);
      return;
    }
    if (!stickyWorkspace) {
      toast.error(t.app.toasts.presenceJumpFailed);
      return;
    }
    setPendingJump({ userId: peer.id, filePath: peer.filePath });
    core.navigation.goWsPath(`${stickyWorkspace.wsName}:${peer.filePath}`);
  };

  if (peers.length === 0) return null;

  return (
    <div className="sn-presence">
      {peers.map((peer) => {
        const profile = profiles[peer.id];
        const avatarSrc = profile?.avatar_url || peer.avatarUrl;
        const label =
          profile?.display_name ||
          profile?.email ||
          peer.name ||
          t.app.common.unknown;
        return (
          <DropdownMenu.DropdownMenu key={peer.key}>
            <DropdownMenu.DropdownMenuTrigger
              render={
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  className="sn-presence-avatar size-7 overflow-hidden rounded-full p-0 text-white"
                  style={avatarSrc ? undefined : { background: peer.color }}
                  title={label}
                  aria-label={label}
                  onPointerDown={(event) => {
                    if (event.metaKey || event.ctrlKey) {
                      event.preventDefault();
                    }
                  }}
                  onClick={(event) => {
                    if (event.metaKey || event.ctrlKey) {
                      event.preventDefault();
                      event.stopPropagation();
                      jumpToPeer(peer);
                    }
                  }}
                />
              }
            >
              {avatarSrc ? (
                <img
                  src={avatarSrc}
                  alt=""
                  className="size-full object-cover"
                />
              ) : (
                (label || '?').slice(0, 1).toUpperCase()
              )}
            </DropdownMenu.DropdownMenuTrigger>
            <DropdownMenu.DropdownMenuContent
              align="end"
              className="w-auto p-3"
            >
              {profile ? (
                <ProfileCard profile={profile} />
              ) : (
                <div className="w-48 text-muted-foreground text-xs">
                  {peer.name}
                </div>
              )}
            </DropdownMenu.DropdownMenuContent>
          </DropdownMenu.DropdownMenu>
        );
      })}
    </div>
  );
}
